import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, Loader2, Check } from 'lucide-react';
import api from '../lib/api';

/**
 * Voice picker rendered inside the Settings "Narration" section when the
 * user has chosen the system-generated fallback. Presents 6 curated voice
 * presets grouped by gender, with a per-voice ▶ Play sample button so
 * users can preview each voice before committing.
 *
 * Props:
 *   current        — currently selected `system_voice_gender` ('female'|'male')
 *   currentVoiceId — currently selected explicit voice id, or null
 *   saving         — boolean: any voice save in flight
 *   onChangeGender — called with 'female' | 'male' when the gender filter changes
 *   onChangeVoiceId — called with a preset id when a voice card is chosen
 */
const VoiceGenderPicker = ({ current, currentVoiceId, saving, onChangeGender, onChangeVoiceId }) => {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const [loadingSampleId, setLoadingSampleId] = useState(null);
  const audioRef = useRef(null);

  // Load the voice catalog from the backend so the frontend never drifts
  // from the server's canonical list.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('/tts/voices')
      .then(({ data }) => { if (!cancelled) setVoices(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setVoices([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    };
  }, []);

  const genderChip = ({ value, label }) => {
    const gActive = current === value;
    return (
      <button
        key={value}
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChangeGender(value); }}
        disabled={saving}
        data-testid={`voice-gender-${value}`}
        className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium border transition-colors disabled:opacity-60 ${
          gActive
            ? 'bg-amber-500 text-white border-amber-500'
            : 'bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 border-zinc-300 dark:border-zinc-700 hover:border-amber-400'
        }`}
      >
        {gActive && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
        {label}
      </button>
    );
  };

  const playSample = async (voiceId, e) => {
    e.preventDefault();
    e.stopPropagation();
    // Stop any currently-playing sample first.
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (playingId === voiceId) {
      setPlayingId(null);
      return;
    }
    setLoadingSampleId(voiceId);
    try {
      const { data } = await api.get(`/tts/sample`, {
        params: { voice: voiceId },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(data);
      const audio = new Audio(url);
      audio.onended = () => { setPlayingId(null); URL.revokeObjectURL(url); };
      audio.onerror = () => { setPlayingId(null); URL.revokeObjectURL(url); };
      audioRef.current = audio;
      setPlayingId(voiceId);
      await audio.play();
    } catch (_) {
      setPlayingId(null);
    } finally {
      setLoadingSampleId(null);
    }
  };

  return (
    <div className="mt-3 space-y-3" data-testid="voice-picker" onClick={(e) => e.preventDefault()}>
      {/* Gender filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-zinc-500 mr-1">Voice type:</span>
        {genderChip({ value: 'female', label: 'Female' })}
        {genderChip({ value: 'male', label: 'Male' })}
        {saving && (
          <span className="inline-flex items-center text-xs text-zinc-500 gap-1">
            <Loader2 size={12} className="animate-spin" /> Saving…
          </span>
        )}
      </div>

      {/* Voice cards */}
      {loading ? (
        <div className="text-xs text-zinc-500 flex items-center gap-2">
          <Loader2 className="animate-spin" size={12} /> Loading voices…
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="voice-card-grid">
          {voices
            // Show voices matching the selected gender OR neutral so the user
            // sees relevant options first. Neutral voices work with either.
            .filter((v) => v.gender === current || v.gender === 'neutral')
            .map((v) => {
              const active = currentVoiceId === v.id;
              const isPlaying = playingId === v.id;
              const isLoadingThis = loadingSampleId === v.id;
              return (
                <div
                  key={v.id}
                  data-testid={`voice-card-${v.id}`}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                    active
                      ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-500/60'
                      : 'border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800/40'
                  }`}
                >
                  <button
                    type="button"
                    onClick={(e) => playSample(v.id, e)}
                    disabled={isLoadingThis}
                    data-testid={`voice-play-${v.id}`}
                    aria-label={`Play sample: ${v.label}`}
                    className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                      isPlaying
                        ? 'bg-amber-500 text-white'
                        : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 hover:bg-amber-100 dark:hover:bg-amber-950/40 hover:text-amber-600 dark:hover:text-amber-400'
                    }`}
                  >
                    {isLoadingThis ? <Loader2 size={16} className="animate-spin" /> : isPlaying ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChangeVoiceId(v.id); }}
                    disabled={saving}
                    data-testid={`voice-select-${v.id}`}
                    className="flex-1 min-w-0 text-left"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{v.label}</span>
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400">
                        {v.accent}
                      </span>
                      {active && <Check size={14} className="text-amber-500" />}
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">{v.description}</div>
                  </button>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default VoiceGenderPicker;
